import type { Client2TrackedTranslation, TranslationTag, Values } from '@ps/web-core-intl';
import { select } from '@ps/web-core-store';
import type { Store } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import type { ValidatorTranslation } from '@ps/web-shared-validators';
import { isArray, memoize } from 'lodash';
import type React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';

import IntlProvider from '../IntlProvider/IntlProvider';
import Translation from '../Translation/Translation';

interface TrackingProps
  extends React.ComponentProps<typeof Translation>,
    Client2TrackedTranslation {}

export const toLocaleDateString = (language: string, dateTime: string) => {
  const convertedFromIsoString = dateTime ? new Date(dateTime) : new Date();
  const timeOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };

  return convertedFromIsoString.toLocaleDateString(language, timeOptions);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isTranslation = (value?: any): value is TranslationTag =>
  !!value?.id && !!value?.defaultMessage;

const validateTranslation = (value?: unknown): value is TranslationTag | TranslationTag[] =>
  isArray(value) ? value.every(isTranslation) : isTranslation(value);

const valuesKey = (values: Values = {}) =>
  Object.values(values)
    .filter((item) => typeof item !== 'object')
    .join('');

export const trackingTranslation = memoize(
  (
    translation: string | TrackingProps | TrackingProps[] | undefined | React.ReactElement,
    store: Store,
    values?: Values
  ): string | undefined => {
    if (typeof translation === 'string') return translation;
    if (!translation || !canUseDOM || !validateTranslation(translation)) return;

    const elem = document.createElement('div');
    const state = store.getState();
    const translations: TrackingProps[] = isArray(translation) ? translation : [translation];
    const locale = select.locale.get(state);

    if (process.env.NODE_ENV === 'test') {
      return translations[0].id;
    }

    try {
      elem.innerHTML = renderToStaticMarkup(
        <Router basename={select.location.getBaseURL(state)}>
          <ReduxProvider store={store}>
            <IntlProvider>
              {translations.reduce<React.ReactNode[]>(
                (
                  memo,
                  { track, ...item }: Client2TrackedTranslation | ValidatorTranslation,
                  index
                ) => {
                  memo.push(
                    <Translation
                      key={item.id}
                      values={values}
                      {...item}
                      defaultMessage={
                        locale === 'en' ? item.defaultMessage : track || item.defaultMessage
                      }
                    />
                  );
                  if (index < translations.length - 1) memo.push(' ');
                  return memo;
                },
                []
              )}
            </IntlProvider>
          </ReduxProvider>
        </Router>
      );
      return elem.innerText;
    } catch {
      return undefined;
    }
  },
  (translation, store, values) => {
    if (typeof translation === 'string') return translation;
    if (!translation || !validateTranslation(translation)) return;
    const translations: TrackingProps[] = isArray(translation) ? translation : [translation];
    return (
      translations
        .map(
          ({ id, defaultMessage, values: inlineValues }) =>
            id + defaultMessage + valuesKey(inlineValues)
        )
        .join('') + valuesKey(values)
    );
  }
);

export const mapDatePickerLocale = (locale: unknown) => {
  switch (locale) {
    case 'es-419':
      return 'es';
    case 'pt-BR':
      return 'pt';
    default:
      return locale;
  }
};
